/**
 * skyAgent action approvals (SkyCRM) — the card's data layer.
 *
 * A slice of its own rather than an addition to `crmSkyAgentApi`, because the two
 * have different lifetimes: that one is the conversation (sessions, history, the
 * SSE turn), this one is the approval ledger, and a proposal outlives the turn
 * that produced it. Keeping them apart also means the drawer's pending-actions
 * list and the chat can invalidate independently.
 *
 * ## Every response can say `enabled: false`
 *
 * The `crm_agent_actions` capability key is a FAIL-CLOSED gate: with it off the
 * routes answer `{ enabled: false }` instead of a payload. That is a NORMAL
 * response, not an error, so the types model it as a union and the UI renders a
 * disabled state. Treating it as an error would put a red banner in front of an
 * operator who has simply not turned the feature on.
 *
 * ## The failure codes are the interesting part
 *
 * Approve is the only call in this product that mutates through the agent, and it
 * can legitimately fail in four ways that are NOT server faults — expired (410),
 * already decided by someone else (409), the world moved so re-validation refused
 * (409 + blockers), and draft-only mode (409). Each is a sentence the counselor
 * needs, so the error shape is modelled rather than collapsed into "failed".
 */
import { baseApi } from "./baseApi";

/** Mirrors `ActionPreview` on the server (crmAgent/crmActionTypes.ts). */
export interface AgentActionPreview {
  title: string;
  lines: Array<{ label: string; value: string }>;
  /** `key` is the payload field an edit writes back to — the approve route merges
   *  by this name, and validates against this same list. */
  editable?: Array<{ key: string; label: string; value: string }>;
}

export type AgentActionStatus =
  | "proposed"
  | "approved"
  | "executed"
  | "rejected"
  | "failed"
  | "expired";

/** What `actionView` returns. `actionType` is the SPEC NAME (`propose_*`). */
export interface AgentActionRow {
  id: string;
  actionType: string;
  status: AgentActionStatus;
  executionMode: "autonomous" | "review" | "draft_only";
  preview: AgentActionPreview;
  result: Record<string, unknown> | null;
  error: string | null;
  decisionKind: string | null;
  editedFields: string[];
  expiresAt: string;
  createdAt: string;
}

type Gated<T> = ({ enabled: true } & T) | { enabled: false };

export interface ApproveResult {
  enabled: true;
  status: "executed";
  summary: string;
  decisionKind: string;
  editedFields: string[];
}

export const crmAgentActionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** The drawer's open proposals. */
    getAgentActions: builder.query<Gated<{ actions: AgentActionRow[] }>, void>({
      query: () => ({ url: "/crm/skyagent/actions", method: "GET" }),
      providesTags: ["CrmAgentAction"],
    }),

    /**
     * One card. The chat renders from this rather than from anything carried in
     * the message text: the marker in the transcript is just an id, so a thread
     * re-opened tomorrow shows each action's CURRENT state (executed, rejected,
     * expired) instead of re-rendering a stale "pending" card that a click would
     * then fail on.
     */
    getAgentAction: builder.query<Gated<{ action: AgentActionRow }>, string>({
      query: (actionId) => ({ url: `/crm/skyagent/actions/${actionId}`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "CrmAgentAction" as const, id }],
    }),

    approveAgentAction: builder.mutation<
      ApproveResult,
      { actionId: string; edits?: Record<string, string> }
    >({
      query: ({ actionId, edits }) => ({
        url: `/crm/skyagent/actions/${actionId}/approve`,
        method: "POST",
        body: edits && Object.keys(edits).length > 0 ? { edits } : {},
      }),
      // Invalidate the list too: applying one action can change what the others
      // would do, and a stale sibling card is a card that fails on click.
      //
      // `CrmBrainFacts` because approving `propose_remember` WRITES A FACT, and the
      // Brain page reads its own cached list. Without this the fact is stored, the
      // card says applied, and the page keeps showing the old list until a manual
      // browser refresh — observed live. Invalidated on EVERY approve rather than
      // only the remember ones because `invalidatesTags` sees the action id, not its
      // type; the facts list is small and an approval is a rare, deliberate click, so
      // the extra refetch is cheaper than threading the type through for this.
      invalidatesTags: (_r, _e, { actionId }) => [
        { type: "CrmAgentAction" as const, id: actionId },
        "CrmAgentAction",
        "CrmBrainFacts",
      ],
    }),

    rejectAgentAction: builder.mutation<
      { enabled: true; status: "rejected" },
      { actionId: string; reason?: string }
    >({
      query: ({ actionId, reason }) => ({
        url: `/crm/skyagent/actions/${actionId}/reject`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: (_r, _e, { actionId }) => [
        { type: "CrmAgentAction" as const, id: actionId },
        "CrmAgentAction",
      ],
    }),
  }),
});

export const {
  useGetAgentActionsQuery,
  useGetAgentActionQuery,
  useApproveAgentActionMutation,
  useRejectAgentActionMutation,
} = crmAgentActionsApi;
